import type { TFunction } from "i18next";

/** Send + Bill Pay: minimum age (years). */
export const DOB_MIN_AGE_SEND_OR_BILLPAY = 18;
/** Receive: minimum age (years). */
export const DOB_MIN_AGE_RECEIVE = 12;
/** All flows: maximum age (years). */
export const DOB_MAX_AGE_YEARS = 125;

function computeFullYearsAge(birthLocalNoon: Date, asOfLocal: Date): number {
	let age = asOfLocal.getFullYear() - birthLocalNoon.getFullYear();
	const m = asOfLocal.getMonth() - birthLocalNoon.getMonth();
	if (m < 0 || (m === 0 && asOfLocal.getDate() < birthLocalNoon.getDate())) {
		age--;
	}
	return age;
}

export type DobAgeOptions = {
	minimumAgeYears?: number;
	maximumAgeYears?: number;
};

/**
 * Kiosk-style MM/DD/YYYY validation (C# DateEntryViewModel.ValidateDate).
 * Messages use keys in en.json / es.json (SENDER_DOB_ERROR_*).
 */
export function validateDobDate(
	t: TFunction,
	day: string,
	month: string,
	year: string,
	mustBeInPast: boolean,
	ageOptions?: DobAgeOptions
): string {
	const dayNum = Number(day);
	if (!Number.isInteger(dayNum) || dayNum < 1 || dayNum > 31) {
		return t("SENDER_DOB_ERROR_INVALID_DAY");
	}

	const monthNum = Number(month);
	if (!Number.isInteger(monthNum) || monthNum < 1 || monthNum > 12) {
		return t("SENDER_DOB_ERROR_INVALID_MONTH");
	}

	const yearNum = Number(year);
	if (!Number.isInteger(yearNum) || yearNum <= 1900) {
		return t("SENDER_DOB_ERROR_INVALID_YEAR");
	}

	const dt = new Date(yearNum, monthNum - 1, dayNum, 12, 0, 0, 0);
	const isValidWholeDate =
		dt.getFullYear() === yearNum &&
		dt.getMonth() === monthNum - 1 &&
		dt.getDate() === dayNum;
	if (!isValidWholeDate) {
		return t("SENDER_DOB_ERROR_INVALID");
	}

	if (mustBeInPast) {
		const today = new Date();
		const todayMidnight = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 0, 0, 0, 0);
		const valueDateMidday = new Date(yearNum, monthNum - 1, dayNum, 12, 0, 0, 0);
		if (valueDateMidday.getTime() >= todayMidnight.getTime()) {
			return t("SENDER_DOB_ERROR_NOT_IN_PAST");
		}
	}

	const birthLocalNoon = new Date(yearNum, monthNum - 1, dayNum, 12, 0, 0, 0);
	const asOf = new Date();
	const asOfLocal = new Date(
		asOf.getFullYear(),
		asOf.getMonth(),
		asOf.getDate(),
		12,
		0,
		0,
		0
	);
	const ageYears = computeFullYearsAge(birthLocalNoon, asOfLocal);

	if (ageOptions?.minimumAgeYears != null && ageYears < ageOptions.minimumAgeYears) {
		return t("SENDER_DOB_ERROR_MIN_AGE", { minAge: ageOptions.minimumAgeYears });
	}
	if (ageOptions?.maximumAgeYears != null && ageYears > ageOptions.maximumAgeYears) {
		return t("SENDER_DOB_ERROR_MAX_AGE", { maxAge: ageOptions.maximumAgeYears });
	}

	return "";
}

/**
 * Manual DOB entry uses MM/DD/YYYY; hydrated consumer profile uses YYYY-MM-DD (see AuthConsumer in MainSendCashScreen).
 * Both are 10 characters — callers must not assume slash-separated parts only.
 */
export function parseDobDisplayToParts(
	trimmed: string
): { day: string; month: string; year: string } | null {
	const s = trimmed.trim();
	const iso = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
	if (iso) {
		return { year: iso[1], month: iso[2], day: iso[3] };
	}
	// US entry: allow M/D/YYYY (e.g. 6/8/1985) as well as MM/DD/YYYY; pad month/day for downstream logic.
	const flexUs = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(s);
	if (flexUs) {
		return {
			month: flexUs[1].padStart(2, "0"),
			day: flexUs[2].padStart(2, "0"),
			year: flexUs[3],
		};
	}
	return null;
}

/** Build a local Date at noon from any display string parseDobDisplayToParts accepts (US or ISO). */
export function dateFromDobDisplayString(trimmed: string): Date | null {
	const p = parseDobDisplayToParts(trimmed.trim());
	if (!p) return null;
	const y = Number(p.year);
	const mo = Number(p.month) - 1;
	const d = Number(p.day);
	const dt = new Date(y, mo, d, 12, 0, 0, 0);
	if (dt.getFullYear() !== y || dt.getMonth() !== mo || dt.getDate() !== d) return null;
	return dt;
}

/** True when the field has a complete M/D/YYYY, MM/DD/YYYY, or ISO date (validation may still fail). */
export function isDobDisplayComplete(raw: string): boolean {
	return parseDobDisplayToParts(raw.trim()) !== null;
}

/**
 * Month segment from digit stream — no leading-zero padding (6 not 06). 10–12 and 01–09 use two digits.
 */
function takeNaturalMonth(d: string, start: number): { text: string; end: number } {
	if (start >= d.length) return { text: "", end: start };
	if (start + 1 < d.length) {
		const two = d.slice(start, start + 2);
		if (two === "10" || two === "11" || two === "12") {
			return { text: two, end: start + 2 };
		}
		if (d[start] === "0") {
			return { text: two, end: start + 2 };
		}
	}
	if (d[start] >= "1" && d[start] <= "9") {
		return { text: d[start], end: start + 1 };
	}
	return { text: d[start], end: start + 1 };
}

/**
 * Day segment — same idea: 10–31 as two digits, 01–09 with leading 0, else single 1–9.
 */
function takeNaturalDay(d: string, start: number): { text: string; end: number } {
	if (start >= d.length) return { text: "", end: start };
	if (start + 1 < d.length) {
		const two = d.slice(start, start + 2);
		const v = parseInt(two, 10);
		if (v >= 10 && v <= 31) {
			return { text: two, end: start + 2 };
		}
		if (d[start] === "0") {
			return { text: two, end: start + 2 };
		}
	}
	if (d[start] >= "1" && d[start] <= "9") {
		return { text: d[start], end: start + 1 };
	}
	return { text: d[start], end: start + 1 };
}

/** Keypad stream (max 8 digits) → m/d/yyyy with natural widths, e.g. 681985 → 6/8/1985, 12121985 → 12/12/1985. */
function formatDobDigitOnly(digits: string): string {
	const d = digits.replace(/\D/g, "").slice(0, 8);
	if (d.length === 0) return "";

	const month = takeNaturalMonth(d, 0);
	let i = month.end;
	if (i >= d.length) return month.text;

	const day = takeNaturalDay(d, i);
	i = day.end;
	if (i >= d.length) return `${month.text}/${day.text}`;

	const year = d.slice(i, i + 4);
	return `${month.text}/${day.text}/${year}`;
}

/**
 * Formats DOB as the user types. Rebuilds from digits (max 8) into m/d/yyyy without padding month/day
 * to two digits unless 01–09, 10–12 (month) or 10–31 / 01–09 (day). Validation still normalizes via parseDobDisplayToParts.
 * Profile YYYY-MM-DD (full or partial with hyphens) is preserved.
 */
export function formatDobInput(raw: string): string {
	const trimmed = (raw ?? "").trim();
	if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
		return trimmed;
	}
	const isoCandidate = trimmed.replace(/[^\d-]/g, "");
	if (isoCandidate.includes("-") && /^(19|20)\d{2}-/.test(isoCandidate)) {
		return isoCandidate.slice(0, 10);
	}

	const digits = (raw ?? "").replace(/\D/g, "").slice(0, 8);
	if (digits.length > 0) {
		return formatDobDigitOnly(digits);
	}
	return "";
}

/** Validates a DOB string as shown in the field (US slashes or ISO dashes from profile). */
export function validateDobDateFromDisplayString(
	t: TFunction,
	displayString: string,
	mustBeInPast: boolean,
	ageOptions?: DobAgeOptions
): string {
	const parts = parseDobDisplayToParts(displayString.trim());
	if (!parts) {
		return t("SENDER_DOB_ERROR_INVALID");
	}
	return validateDobDate(t, parts.day, parts.month, parts.year, mustBeInPast, ageOptions);
}
